import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Star } from "@/components/landing/Logo";
import { StoreBadges } from "@/components/landing/StoreBadges";
import { Reveal } from "@/components/landing/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getFeature, getFeatureUi, getFeatureLinks } from "@/data/featurePages";
import { useLang } from "@/i18n/LanguageContext";

export default function Feature() {
  const { slug } = useParams();
  const { lang } = useLang();
  const f = getFeature(lang, slug);
  const ui = getFeatureUi(lang);

  useEffect(() => {
    if (!f) return;
    const timer = setTimeout(() => {
      document.title = f.metaTitle;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", f.metaDescription);
    }, 0);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "feature-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: lang,
      mainEntity: f.faq.map((x) => ({
        "@type": "Question",
        name: x.q,
        acceptedAnswer: { "@type": "Answer", text: x.a },
      })),
    });
    document.head.appendChild(ld);
    return () => {
      clearTimeout(timer);
      document.getElementById("feature-ld")?.remove();
    };
  }, [f, lang, slug]);

  if (!f) return <Navigate to="/" replace />;
  const others = getFeatureLinks(lang).filter((x) => x.slug !== slug);

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} data-testid={`feature-page-${slug}`}>
      <Navbar />

      <section className="mx-auto max-w-5xl px-5 sm:px-8 pt-32 pb-16 sm:pt-40">
        <Reveal>
          <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.2em" }}>
            <Star size={12} /> {f.kicker}
          </p>
          <h1 className="mt-5 max-w-3xl font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08]" data-testid="feature-title">
            {f.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{f.sub}</p>
          <StoreBadges className="mt-9" />
        </Reveal>
      </section>

      <section className="border-t" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16">
          <Reveal>
            <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> {ui.benefitsKicker}
            </p>
          </Reveal>
          <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="feature-benefits">
            {f.benefits.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full rounded-3xl border p-7" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
                  <span className="font-serif-display text-3xl font-semibold" style={{ color: "var(--gold)" }}>0{i + 1}</span>
                  <h2 className="mt-3 font-serif-display text-xl font-semibold">{b.h}</h2>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{b.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16" data-testid="feature-steps">
        <Reveal>
          <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {ui.stepsKicker}
          </p>
        </Reveal>
        <div className="mt-8 space-y-6">
          {f.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex items-start gap-5 rounded-3xl border p-6" style={{ borderColor: "var(--line)" }}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "var(--camel)" }}>
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-serif-display text-lg font-semibold">{s.h}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{s.p}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t" style={{ borderColor: "var(--line)" }}>
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16" data-testid="feature-faq">
          <Reveal>
            <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> {ui.faqKicker}
            </p>
          </Reveal>
          <Accordion type="single" collapsible className="mt-6">
            {f.faq.map((x, i) => (
              <AccordionItem key={i} value={`faq-${i}`} style={{ borderColor: "var(--line)" }}>
                <AccordionTrigger className="text-left font-medium hover:no-underline" data-testid={`feature-faq-q-${i}`}>{x.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{x.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="border-t text-center" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
        <div className="mx-auto max-w-3xl px-5 py-20" data-testid="feature-cta">
          <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.2em" }}>
            <Star size={12} /> {ui.ctaKicker}
          </p>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl font-semibold">{f.ctaTitle}</h2>
          <p className="mx-auto mt-4 max-w-md text-base" style={{ color: "var(--muted-warm)" }}>{f.ctaSub}</p>
          <div className="mt-8 flex justify-center"><StoreBadges /></div>
          <div className="mt-14">
            <p className="text-xs uppercase font-medium" style={{ color: "var(--muted-warm)", letterSpacing: "0.14em" }}>{ui.moreTitle}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/features/${o.slug}`}
                  data-testid={`feature-link-${o.slug}`}
                  className="rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)]"
                  style={{ borderColor: "var(--line)", background: "var(--surface)" }}
                >
                  {o.kicker} <ArrowRight size={13} className="ml-1 inline" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
